import { useEffect, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import { green } from "@mui/material/colors";

import { withTranslation } from "react-i18next";
import { debounce, isEmpty } from "lodash";
import { addDays, format } from "date-fns";
import { DefaultTooltipContent } from "recharts";

import fpProdSalesStockStatusService from "@plannel/services/fp/fp-prod-sales-stock-status-service";
import numberUtil from "@plannel/utils/number-util";
import VersionSearchGroup from "@plannel/pages/factory-plan/common/component/VersionSearchGroup";
import PeriodSearchGroup, { BUCKET_CASE } from "@plannel/pages/factory-plan/common/component/PeriodSearchGroup";
import SearchOptsGroupRetrieve from "@plannel/pages/factory-plan/common/component/SearchOptsGroupRetrieve";
import FpGridSetting, {
  readHide, buildGroupColumnPair, REPORT_TREE_GRID_OPTIONS, buildReportStatusBar,
  applyColumnOrder, buildDayBucketPivotColumnDefs, consumePendingFocus
} from "@plannel/pages/factory-plan/common/grid";
import { buildXAxisSortMap } from "@plannel/pages/factory-plan/common/chart";
import { getFilterStore } from "@plannel/components/aggrid/FilterStatusPanel";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import SplitterContainer from "@plannel/components/SplitterContainer";
import PivotChartComposed from "@plannel/components/rechart/PivotChartComposed";
import Snackbar from "@plannel/components/Snackbar";
import { DownloadButton } from "@plannel/components/ActionIconButton";
import ColorUtil from "@plannel/pages/utils/color-util";
import reduxUtil from "@plannel/utils/redux-util";
import debug from "@plannel/utils/debug-util";

const { snackBarMessageColor } = ColorUtil;

// old fpSettings = { searchZone: 14 }(local fallback, FP_SRH_RANGE DB 세팅 로드 전 임시값이 아니라
// 이 화면의 실제 확정값) — PlanResult(9)/MaterialPsi(9)와 다른 값이지만 화면마다 DB에 저장된 값이
// 다를 수 있다는 전제(fp-report-screen-guide.md §A-3c)에서 old 소스에 박혀 있던 값 그대로 이식한다.
// TODO(MIG-T2): [prodsalesstockstatus/ProdSalesStockStatus] 조회조건 세팅 3종(버킷 목록/검색필드
//   표시/검색구간)의 테넌트별 커스터마이징 미이관.
//   old: search-option/bucket · search-option/srh-options · setting FP_SRH_RANGE를 각각 DB에서 조회
//   사유: 정적 구성(JSON 카탈로그/고정 카탈로그/상수)으로 전환 — 가이드 §A-3~§A-3d에서 결정
const SEARCH_ZONE_DAYS = 14;

// old SearchOptsGroup은 화면에서 stageCdNm/itemCdNm 둘 다 getValues()로 읽어 보내지만,
// ProdSalesStockStatusQueryRepository#makeWhereClause는 itemCdNm만 실제로 contains 조건으로
// 소비하고 stageCdNm은 어느 where절에도 읽지 않는 죽은 파라미터다(PsiDetail — 같은 백엔드
// 쿼리 클래스를 쓰는 `/detail` 변종 — 이관 시 먼저 확인된 사실, deferred-features.md 참고).
// §A-3b 원칙(old가 "실제로" 쓰는 필드만 고정 카탈로그)에 따라 itemCdNm 하나만 노출한다.
const SEARCH_FIELD_LABELS = { itemCdNm: "FP_ITEM" };
const SEARCH_FIELD_KEYS = Object.keys(SEARCH_FIELD_LABELS);

// old InputField(type="select", name="itemClass", defaultValue:'P') — SearchOptsGroup과 달리
// 화면 캐스케이드에 얽히지 않는 독립 콤보라 SearchOptsGroupRetrieve에 넣지 않고 화면이 직접
// controlled state로 소유한다(선택형 조건이라 §A-9b 정책대로 값이 바뀌면 즉시 재조회).
const ITEM_CLASS_OPTIONS = [
  { value: "P", label: "FP_ITEM_CLASS_CD_P" },
  { value: "I", label: "FP_ITEM_CLASS_CD_I" },
  { value: "M", label: "FP_ITEM_CLASS_CD_M" }
];
const DEFAULT_ITEM_CLASS_CD = "P";

const PARAM_DATE_FORMAT = "yyyyMMdd";

// old INDICATOR_CONFIG(ProdSalesStockStatusChart.jsx, themeStore.t3smartscm.fpChartPriority) 이식 —
// 백엔드(ProdSalesStockStatusService#getPsiData, isDetail=false)가 그룹당 항상 이 4개
// analysisIndicator 행만 내려주므로(PlanResult의 itemCd처럼 클릭 시점에 동적으로 시리즈를 조립할
// 필요가 없다) MaterialPsi/InoutStatus와 동일하게 화면 고정 배열로 선언한다.
// direction: 백엔드가 늘 양수(절대값)로 내려주므로(getPsiData의 pivotData 계산 참고) 'minus'인
// 지표(소비)만 프론트에서 음수로 뒤집어 그린다 — 기초/기말재고는 선(기말재고는 점선), 생산/소비는
// 서로 반대 방향 스택(순증감 워터폴)으로 old와 동일한 시각 효과를 recharts 조합만으로 재현한다
// (ChartComposed/PivotChartComposed 수정 없이 stackId 분리 + 부호 반전만으로 충분 — 공용 파일
// 변경 불필요, 차트 스코프 축소 없이 old의 실제 조합 차트를 그대로 구현).
const FP_BEGINNING_STOCK = "FP_BEGINNING_STOCK";
const FP_ENDING_STOCK = "FP_ENDING_STOCK";
const FP_PRODUCTION = "FP_PRODUCTION";
const FP_CONSUMPTION = "FP_CONSUMPTION";
const INDICATOR_ORDER = [FP_BEGINNING_STOCK, FP_PRODUCTION, FP_CONSUMPTION, FP_ENDING_STOCK];
const INDICATOR_CONFIG = {
  [FP_BEGINNING_STOCK]: { type: "line", color: "#4472C4", dashed: false, direction: "plus" },
  [FP_ENDING_STOCK]: { type: "line", color: "#4472C4", dashed: true, direction: "plus" },
  [FP_PRODUCTION]: { type: "bar", color: "#6DE36F", stackId: "production", direction: "plus" },
  [FP_CONSUMPTION]: { type: "bar", color: "#FC5F67", stackId: "consumption", direction: "minus" }
};

// old ProdSalesStockStatusChart.jsx는 getDefaultChartOptions(useAbsoluteValue=true)로 축/툴팁 값에
// 항상 Math.abs()를 적용했다(소비가 막대는 0 아래로 내려가도 값 자체는 부호 없이 보이는 이유,
// old chart.js의 isAbsolute 분기 확인). PivotChartComposed(공용)의 기본 툴팁 포맷터
// (formatterByNameForToolTip)는 값이 음수면 "-"를 무조건 다시 붙이는 로직이 하드코딩돼 있어 화면별로
// 못 끈다 — 공용 파일은 안 건드리고, recharts가 제공하는 customTooltip 확장 포인트로 기본 툴팁
// 렌더러(DefaultTooltipContent)를 그대로 재사용하면서 formatter만 Math.abs 적용 버전으로 바꿔
// old와 동일하게 무부호로 표시한다(모양은 기본 툴팁과 동일, 값 포맷만 교체).
const AbsValueTooltip = (props) => (
  <DefaultTooltipContent {...props} formatter={(value) => numberUtil.formatNumber(Math.abs(value))} />
);

// 트리(행 그룹) 계층 — 공장 > 스테이지 > 품목 > 인벤토리 > 품목구분 > (leaf)분석지표. old 컬럼정의의
// itemGroup(itemCd/itemNm/inventoryCd/inventoryNm/itemClassCd 전부 mergeRule)까지 포함해 old가
// mergeRule을 건 컬럼 전부를 체이닝한다(Tree Data가 아님 — LocalTreeDataProvider가 아니라
// mergeRule이고 백엔드 응답도 평평한 목록, fp-report-screen-guide.md §A-6b 판단표 · ResrcPlanResult/
// InoutStatus와 동일 해석). 화면 지시사항 기준으로는 plant/stage/item 3그룹이지만, AG Grid 행
// 그룹 체이닝은 old의 mergeRule 컬럼 순서(plant→stage→item→inventory→itemClassCd)를 그대로 따른다.
const GROUP_FIELDS = ["plantCd", "plantNm", "stageCd", "stageNm", "itemCd", "itemNm", "inventoryCd", "inventoryNm"];

const PLANT_CD_COL_ID = "plantCdGrp";
const PLANT_NM_COL_ID = `PLANT_GRP_NM${GridUtils.IGNORE_P13N_COLLID}`;
const STAGE_CD_COL_ID = "stageCdGrp";
const STAGE_NM_COL_ID = `STAGE_GRP_NM${GridUtils.IGNORE_P13N_COLLID}`;
const ITEM_CD_COL_ID = "itemCdGrp";
const ITEM_NM_COL_ID = `ITEM_GRP_NM${GridUtils.IGNORE_P13N_COLLID}`;
const INVENTORY_CD_COL_ID = "inventoryCdGrp";
const INVENTORY_NM_COL_ID = `INVENTORY_GRP_NM${GridUtils.IGNORE_P13N_COLLID}`;
const ITEM_CLASS_CD_COL_ID = "itemClassCdGrp";

// CustomDetailHeader가 "코드 컬럼 화살표 → 명칭 컬럼 표시/숨김" 상태를 redux viewState에 저장할 때
// 쓰는 키 — 4쌍이 서로 다른 키를 써야 상태를 덮어쓰지 않는다(다른 Report 화면과 동일 이유).
const PLANT_NM_HIDE_KEY = "plantNmColsHide";
const STAGE_NM_HIDE_KEY = "stageNmColsHide";
const ITEM_NM_HIDE_KEY = "itemNmColsHide";
const INVENTORY_NM_HIDE_KEY = "inventoryNmColsHide";

const ProdSalesStockStatus = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);

  const [versionState, setVersionState] = useState({
    planScope: "", planningDate: null, versionCd: "", plantCd: [], selectedVersion: null
  });
  const [periodState, setPeriodState] = useState({
    startDate: null, endDate: null, bucketType: "", isEntirePlanPeriod: true
  });
  // itemClassCd — old InputField(select, defaultValue:'P'), SearchOptsGroup과 달리 화면이 controlled
  // state로 직접 소유하는 독립 콤보(선택형 조건이라 §A-9b 정책대로 값이 바뀌면 즉시 재조회된다).
  const [itemClassCd, setItemClassCd] = useState(DEFAULT_ITEM_CLASS_CD);
  // itemCdNm — Report는 전체 화면 공통으로 조회 버튼이 없다(§A-9b). 자유 텍스트라 매 키 입력마다
  // 재조회하면 안 되므로 controlled state가 아니라 ref(uncontrolled)로 두고, SearchOptsGroupRetrieve
  // (TextFieldSearch 기반) 안의 Enter/검색아이콘으로만 커밋한다.
  const itemCdNmRef = useRef("");

  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });

  const gridRef = useRef();
  const [rowData, setRowData] = useState([]);

  const [chartData, setChartData] = useState([]);
  const [chartLabels, setChartLabels] = useState([]);

  // 응답 순서 뒤바뀜 방지 — 조회(그리드)와 차트는 서로 다른 요청이라 가드도 2개(§0-8).
  const latestGridReqRef = useRef(0);
  const latestChartReqRef = useRef(0);
  // old prevChartParamsRef — 같은 (plant, stage, item, inventory) 조합이면 차트를 다시 조회하지 않는다.
  const prevChartParamsRef = useRef(null);
  const pendingFocusRef = useRef(null);
  // columnDefs/파라미터 조립을 조회 응답 시점 콜백에서 다시 만들어 넘기므로(§A-6), 최신 조회조건을
  // 콜백에서 읽기 위한 ref. itemClassCd도 controlled state라 여기 같이 담아야 클로저 안에서 최신값을
  // 읽는다(versionState/periodState와 동일 이유).
  const searchStateRef = useRef({ versionState, periodState, itemClassCd });
  searchStateRef.current = { versionState, periodState, itemClassCd };

  // planScope→plan-dts→planningDate→versions→versionCd/plantCd→기간 재계산 캐스케이드가 여러
  // 렌더에 걸쳐 순차 커밋되면서 그 각 단계가 독립적으로 자동조회를 트리거해 같은 prod-sales-stock-status가
  // 여러 번 호출되는 문제를 막는 디바운스(PlanResult §A-9b와 동일 원리).
  const debouncedRetrieveRef = useRef();
  if (!debouncedRetrieveRef.current) {
    debouncedRetrieveRef.current = debounce(() => retrieveProdSalesStockStatus(), 200);
  }
  useEffect(() => () => debouncedRetrieveRef.current.cancel(), []);

  // 화면을 떠날 때 하단 상태바의 선택 브레드크럼(전역 스토어)을 비운다.
  useEffect(() => () => getFilterStore(viewName).clear(), [viewName]);

  const defaultGridMemo = useMemo(() => {
    const base = FpGridSetting({ title, viewName });
    return {
      ...base,
      // 조회 전용 화면 — 공용 DefaultGridSetting의 defaultColDef.editable:true를 끈다.
      defaultColDef: { ...base.defaultColDef, editable: false },
      // 이 화면은 old도 행 전체가 아니라 피벗 데이터 셀(강조)에만 스타일을 걸었다(아래
      // pivotCellStyle 참고) — 공용 DataState.getRowStyles(편집 상태 표시용, read-only
      // 화면엔 무의미)를 끈다.
      getRowStyle: () => null,
      // 행 그룹(트리) 3종 세트 — PlanResult/ResrcPlanResult/MaterialPsi와 동일한 조합.
      ...REPORT_TREE_GRID_OPTIONS,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 핵심 지표(기말재고) 강조 — 분석 지표 컬럼에 건다. 배경색만(green[50]), 굵게는 안 씀 — 아래
  // pivotCellStyle과 동일 기준으로 분석 지표 컬럼~피벗 컬럼 범위에만 적용된다(MaterialPsi와 동일).
  const analysisIndicatorHighlightStyle = (params) => (
    params.data?.analysisIndicator === FP_ENDING_STOCK ? { backgroundColor: green[50] } : null
  );

  // 고정(왼쪽 pinned) 컬럼 — old fixColumns(plantCd/plantNm/stageCd/stageNm/itemCd/itemNm/
  // inventoryCd/inventoryNm/itemClassCd/analysisIndicator, 10개)와 1:1. 공장/스테이지/품목/인벤토리는
  // 행 그룹(코드+명칭 쌍)으로, itemClassCd는 코드/명칭 쌍이 없는 단일 그룹 표시 컬럼(번역 라벨만
  // 있음)으로, analysisIndicator는 트리의 leaf(실제 데이터 행을 구분하는 값)라 field 컬럼이다.
  const fixedColumnDefs = useMemo(() => [
    ...GROUP_FIELDS.map(field => ({
      headerName: field, field, colId: field, rowGroup: true, hide: true, suppressColumnsToolPanel: true
    })),
    // itemClassCd는 값 자체가 코드(P/I/M)라 다국어 라벨 변환이 필요한 유일한 그룹 필드다 — 위
    // GROUP_FIELDS.map이 만드는 범용 hidden 컬럼엔 valueFormatter를 못 넣어서 그룹 셀이 번역 없이
    // 원본 코드로 노출되던 버그가 있었다(PsiDetail.js와 동일 이슈, 2026-08-11 수정). 그룹 셀
    // 렌더링이 이 hidden 필드 컬럼과 아래 showRowGroup 표시 컬럼 중 어느 쪽 valueFormatter를 쓰는지
    // 확실치 않아 양쪽에 동일하게 건다.
    {
      headerName: "itemClassCd", field: "itemClassCd", colId: "itemClassCd", rowGroup: true, hide: true,
      suppressColumnsToolPanel: true,
      valueFormatter: (params) => (params.value ? t(`FP_ITEM_CLASS_CD_${params.value}`) : params.value)
    },

    ...buildGroupColumnPair({
      viewName, hidden: readHide(reduxViewState, PLANT_NM_HIDE_KEY),
      cdColId: PLANT_CD_COL_ID, cdField: "plantCd", cdHeader: "FP_PLANT_CD",
      nmColId: PLANT_NM_COL_ID, nmField: "plantNm", nmHeader: "PLANT_NM",
      hideKey: PLANT_NM_HIDE_KEY, cdWidth: 150, nmWidth: 150
    }),
    ...buildGroupColumnPair({
      viewName, hidden: readHide(reduxViewState, STAGE_NM_HIDE_KEY),
      cdColId: STAGE_CD_COL_ID, cdField: "stageCd", cdHeader: "FP_STAGE_CD",
      nmColId: STAGE_NM_COL_ID, nmField: "stageNm", nmHeader: "FP_STAGE_NM",
      hideKey: STAGE_NM_HIDE_KEY, cdWidth: 150, nmWidth: 150
    }),
    ...buildGroupColumnPair({
      viewName, hidden: readHide(reduxViewState, ITEM_NM_HIDE_KEY),
      cdColId: ITEM_CD_COL_ID, cdField: "itemCd", cdHeader: "FP_ITEM_CD",
      nmColId: ITEM_NM_COL_ID, nmField: "itemNm", nmHeader: "ITEM_NM",
      hideKey: ITEM_NM_HIDE_KEY, cdWidth: 100, nmWidth: 150
    }),
    ...buildGroupColumnPair({
      viewName, hidden: readHide(reduxViewState, INVENTORY_NM_HIDE_KEY),
      cdColId: INVENTORY_CD_COL_ID, cdField: "inventoryCd", cdHeader: "FP_INVENTORY_CD",
      nmColId: INVENTORY_NM_COL_ID, nmField: "inventoryNm", nmHeader: "FP_INVENTORY_NM",
      hideKey: INVENTORY_NM_HIDE_KEY, cdWidth: 100, nmWidth: 150
    }),

    // itemClassCd — old displayCallback가 transLangKey('FP_ITEM_CLASS_CD_' + value)로 번역해서
    // 보여준다. 코드/명칭 쌍이 아니라 단일 컬럼(값 자체가 이미 P/I/M 코드라 별도 명칭 필드가 없음)
    // 이라 CustomDetailHeader 토글도 없다.
    {
      headerName: "FP_ITEM_CLASS_CD", colId: ITEM_CLASS_CD_COL_ID, showRowGroup: "itemClassCd", width: 80,
      pinned: "left", lockPinned: true, type: ["nonEditableColumn"], cellRenderer: "agGroupCellRenderer",
      cellRendererParams: { suppressCount: true },
      valueFormatter: (params) => (params.value ? t(`FP_ITEM_CLASS_CD_${params.value}`) : params.value)
    },

    // leaf(분석 지표) — old displayCallback가 항상 transLangKey(value)로 번역해서 보여준다.
    {
      headerName: "FP_ANALYSIS_INDICATOR", field: "analysisIndicator", colId: "analysisIndicator", width: 130,
      pinned: "left", lockPinned: true, type: ["nonEditableColumn"],
      valueFormatter: (params) => t(params.value),
      cellStyle: analysisIndicatorHighlightStyle
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t]);

  // 일버킷일 때 자식 헤더로 오는 요일 코드 — old setColumnColorOnSunSat 대응(§A-6f, 다른 Report
  // 화면과 동일 처리).
  // old setCrossTabGridLayoutAndOptions의 columnProperty.styleCallback(피벗 데이터 셀 전용, 고정
  // 컬럼엔 안 걸림) — analysisIndicator==='FP_ENDING_STOCK' 행은 green[50] 배경(굵게 없음, 위
  // analysisIndicatorHighlightStyle과 동일 기준).
  const pivotCellStyle = (params) => (
    params.data?.analysisIndicator === FP_ENDING_STOCK ? { backgroundColor: green[50] } : null
  );

  // 요일 코드 번역+토/일 강조색은 common/grid.js buildDayBucketPivotColumnDefs가 처리한다
  // (§A-6d/§A-6f). pivotCellStyle은 이 화면 고유 강조라 childExtraProps로 얹는다.
  const buildPivotColumnDefs = (crosstabHeader) => buildDayBucketPivotColumnDefs(crosstabHeader, t, {
    childExtraProps: () => ({ cellStyle: pivotCellStyle })
  });

  // old setCrossTabGridDataWithBucket — pivotData[i]를 crosstabHeader[i] 필드로 펼친다.
  const buildRowData = (crosstabHeader, data) => (data ?? []).map(row => {
    const flat = {
      plantCd: row.plantCd, plantNm: row.plantNm,
      stageCd: row.stageCd, stageNm: row.stageNm,
      itemCd: row.itemCd, itemNm: row.itemNm,
      inventoryCd: row.inventoryCd, inventoryNm: row.inventoryNm,
      itemClassCd: row.itemClassCd, analysisIndicator: row.analysisIndicator
    };
    (crosstabHeader ?? []).forEach((header, index) => {
      flat[header] = row.pivotData?.[index];
    });
    return flat;
  });

  const toParamDate = (date) => (date ? format(date, PARAM_DATE_FORMAT) : null);

  // 조회/차트 공통 파라미터(old searchParams의 공통부). itemClassCd는 old도 매 요청(그리드/차트)에
  // 항상 실어 보내던 값이라 여기서 공통으로 담는다(LookupQueryParameter#itemClassCd, 백엔드는
  // ProdSalesStockStatusQueryRepository에서 eq 조건으로 소비).
  const buildBaseParams = () => {
    const { versionState: version, periodState: period, itemClassCd: currentItemClassCd } = searchStateRef.current;
    return {
      viewId: viewName,
      subId: "",
      planScope: version.planScope,
      versionCd: version.versionCd,
      itemCdNm: itemCdNmRef.current,
      itemClassCd: currentItemClassCd,
      bucketType: period.bucketType,
      fromDt: toParamDate(period.startDate),
      toDt: toParamDate(period.endDate),
      usePlanPeriod: !!period.isEntirePlanPeriod
    };
  };

  const clearChart = () => {
    prevChartParamsRef.current = null;
    setChartData([]);
    setChartLabels([]);
    getFilterStore(viewName).clear();
  };

  // 하단 상태바 브레드크럼 — 공장>스테이지>품목>인벤토리 4단을 FilterStatusPanel의 고정 슬롯
  // (originLoc/destLoc/customer/item) 순서에 맞춰 싣는다(PlanResult/InoutStatus와 동일한 슬롯
  // 재활용 원칙, §A-7).
  const updateStatusBreadcrumb = (target) => {
    getFilterStore(viewName).setValue({
      originLocCd: target.plantCd ?? null, originLocCdHeader: t("FP_PLANT_CD"),
      originLocNm: target.plantNm ?? null, originLocNmHeader: t("PLANT_NM"),
      destLocCd: target.stageCd ?? null, destLocCdHeader: t("FP_STAGE_CD"),
      destLocNm: target.stageNm ?? null, destLocNmHeader: t("FP_STAGE_NM"),
      customerCd: target.itemCd ?? null, customerCdHeader: t("FP_ITEM_CD"),
      customerNm: target.itemNm ?? null, customerNmHeader: t("ITEM_NM"),
      itemCd: target.inventoryCd ?? null, itemCdHeader: t("FP_INVENTORY_CD"),
      itemNm: target.inventoryNm ?? null, itemNmHeader: t("FP_INVENTORY_NM")
    });
  };

  // 클릭 기준으로 차트를 다시 조회한다. old updateChartFromGrid와 동일 — 상위 레벨을 클릭하면
  // 하위 코드는 비워서 보낸다(백엔드가 useGrouping:true일 때 이 레벨로 집계, GroupingLevel 참고).
  // ⚠️ old updateChartFromGrid/loadChartData는 stageCd 파라미터를 받아놓고도 실제 searchParams에는
  // 담지 않는 버그가 있었다(스테이지 행을 클릭해도 백엔드 집계 레벨이 그대로 PLANT였다) — 다른
  // 화면들과 마찬가지로(§A-9 "old의 기간 검증 연산자 우선순위 버그" 선례) 버그를 그대로 옮기지
  // 않고 의도대로(스테이지 클릭 시 스테이지 레벨까지 집계) 구현한다.
  const loadChart = (target) => {
    const { versionState: version } = searchStateRef.current;
    if (!version.planScope || !version.versionCd) return;

    updateStatusBreadcrumb(target);

    const nextParams = {
      plantCd: target.plantCd ?? "", stageCd: target.stageCd ?? "",
      itemCd: target.itemCd ?? "", inventoryCd: target.inventoryCd ?? ""
    };
    const prev = prevChartParamsRef.current;
    if (prev && Object.keys(nextParams).every(field => prev[field] === nextParams[field])) return;
    prevChartParamsRef.current = nextParams;

    const reqId = ++latestChartReqRef.current;
    const params = { ...buildBaseParams(), ...nextParams, useGrouping: true };

    // TODO(MIG-T2): [prodsalesstockstatus/ProdSalesStockStatus] 그리드 컬럼 필터(값 목록 체크박스) 미이관.
    //   old: plantCd/stageCd/itemCd/inventoryCd/itemClassCd/analysisIndicator에 RealGrid autoFilter,
    //        필터 선택값을 차트 조회의 changes(FilterParameter)로 함께 전송
    //   사유: 행 그룹(트리)으로 전환하면서 코드 컬럼이 hide:true인 데이터 컬럼이 돼 헤더 필터
    //        아이콘을 띄울 자리가 없어졌다. 백엔드는 changes를 계속 받으므로 2차에 트리거 UI만
    //        얹으면 된다(가이드 §A-6e와 동일한 결정) — 지금은 빈 필터로 보낸다.
    fpProdSalesStockStatusService.getProdSalesStockStatusChart(params, {})
      .then(response => {
        if (reqId !== latestChartReqRef.current) return;

        const { chartHeader, data } = response.data ?? {};
        const rows = data ?? [];

        if (isEmpty(rows) || isEmpty(chartHeader)) {
          setChartData([]);
          setChartLabels([]);
          return;
        }

        setChartLabels(chartHeader);
        // old makeChartDataset — 지표별로 한 행. 백엔드가 항상 양수(절대값)로 내려주므로
        // (ProdSalesStockStatusService#getPsiData 확인) direction:'minus'인 지표(소비)만
        // 프론트에서 음수로 뒤집어 워터폴 형태로 그린다(§ 상단 INDICATOR_CONFIG 주석 참고).
        setChartData(rows.map(r => {
          const config = INDICATOR_CONFIG[r.analysisIndicator];
          const sign = config?.direction === "minus" ? -1 : 1;
          const point = { analysisIndicator: r.analysisIndicator };
          chartHeader.forEach((header, index) => {
            const raw = r.pivotData?.[index];
            point[header] = raw != null ? sign * Math.abs(raw) : null;
          });
          return point;
        }));
      })
      .catch(e => {
        prevChartParamsRef.current = null;
        debug(e);
      });
  };

  const retrieveProdSalesStockStatus = () => {
    const { versionState: version, periodState: period } = searchStateRef.current;

    if (!version.planScope || !version.versionCd) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("FP_MSG_SELECT_VERSION") });
      return;
    }
    if (!period.isEntirePlanPeriod && (!period.startDate || !period.endDate)) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("FP_MSG_SELECT_PLAN_PERIOD") });
      return;
    }

    const reqId = ++latestGridReqRef.current;
    const params = { ...buildBaseParams(), plantCds: version.plantCd, useGrouping: false };

    gridRef.current?.api?.setGridOption("loading", true);

    fpProdSalesStockStatusService.getProdSalesStockStatuses(params)
      .then(response => {
        if (reqId !== latestGridReqRef.current) return;

        const { crosstabHeader, data } = response.data ?? {};
        const api = gridRef.current?.api;
        if (!api) return;

        const nextColumnDefs = [...fixedColumnDefs, ...buildPivotColumnDefs(crosstabHeader)];
        GridUtils.setColumnDefs({ api, columnDefs: nextColumnDefs, viewName, initState: false });
        applyColumnOrder(api, nextColumnDefs);

        const rows = buildRowData(crosstabHeader, data);
        setRowData(rows);

        if (isEmpty(rows)) {
          clearChart();
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
          return;
        }

        // old: 조회 직후 첫 행의 공장 기준으로 차트를 그리고, 그리드의 선택 셀도 그 행으로 옮긴다.
        pendingFocusRef.current = { rowIndex: 0, colId: PLANT_CD_COL_ID };
        prevChartParamsRef.current = null;
        loadChart({ plantCd: rows[0].plantCd, plantNm: rows[0].plantNm });
      })
      .catch(e => {
        if (reqId === latestGridReqRef.current) clearChart();
        debug(e);
      })
      .finally(() => {
        if (reqId === latestGridReqRef.current) gridRef.current?.api?.setGridOption("loading", false);
      });
  };

  const onGridReady = (params) => {
    GridUtils.setColumnDefs({ ...params, columnDefs: fixedColumnDefs, viewName, initState: false });
  };

  const onRowDataUpdated = () => consumePendingFocus(gridRef, pendingFocusRef);

  const PLANT_LEVEL_COL_IDS = new Set([PLANT_CD_COL_ID, PLANT_NM_COL_ID]);
  const STAGE_LEVEL_COL_IDS = new Set([STAGE_CD_COL_ID, STAGE_NM_COL_ID]);
  const ITEM_LEVEL_COL_IDS = new Set([ITEM_CD_COL_ID, ITEM_NM_COL_ID]);
  const INVENTORY_LEVEL_COL_IDS = new Set([INVENTORY_CD_COL_ID, INVENTORY_NM_COL_ID]);

  // 클릭한 셀에서 차트를 그릴 드릴다운 대상을 뽑는다(old updateChartFromGrid의
  // `column.startsWith('plant'|'stage'|'item')` 분기를 트리 표시 컬럼 colId 기준으로 옮긴 것).
  // itemClassCd/analysisIndicator/피벗 셀 클릭은 old도 별도 분기가 없어(else 없음) 그 행의 전체
  // 값(가장 깊은 인벤토리 레벨까지)을 그대로 쓴다 — fallback으로 row 전체를 반환한다.
  const resolveChartTarget = (params) => {
    const node = params.node;
    if (!node) return null;

    if (node.group) {
      const target = {};
      for (let current = node; current; current = current.parent) {
        if (current.field) target[current.field] = current.key;
      }
      return isEmpty(target) ? null : target;
    }

    const row = node.data;
    if (!row) return null;

    const colId = params.column?.getColId() ?? "";
    if (PLANT_LEVEL_COL_IDS.has(colId)) {
      return { plantCd: row.plantCd, plantNm: row.plantNm };
    }
    if (STAGE_LEVEL_COL_IDS.has(colId)) {
      return { plantCd: row.plantCd, plantNm: row.plantNm, stageCd: row.stageCd, stageNm: row.stageNm };
    }
    if (ITEM_LEVEL_COL_IDS.has(colId)) {
      return {
        plantCd: row.plantCd, plantNm: row.plantNm, stageCd: row.stageCd, stageNm: row.stageNm,
        itemCd: row.itemCd, itemNm: row.itemNm
      };
    }
    if (INVENTORY_LEVEL_COL_IDS.has(colId)) {
      return {
        plantCd: row.plantCd, plantNm: row.plantNm, stageCd: row.stageCd, stageNm: row.stageNm,
        itemCd: row.itemCd, itemNm: row.itemNm, inventoryCd: row.inventoryCd, inventoryNm: row.inventoryNm
      };
    }
    return row;
  };

  const onCellClicked = (params) => {
    const target = resolveChartTarget(params);
    if (target) loadChart(target);
  };

  const statusBar = useMemo(() => buildReportStatusBar(viewName), [viewName]);

  // 버전이 바뀌면 조회 구간을 다시 계산한다(old setVersionPeriod) — 다른 Report 화면과 동일 원리.
  useEffect(() => {
    const version = versionState.selectedVersion;
    if (!version?.startTs) {
      setPeriodState(prev => (prev.startDate || prev.endDate) ? { ...prev, startDate: null, endDate: null } : prev);
      return;
    }

    const startDate = new Date(version.startTs);
    const endDate = addDays(startDate, SEARCH_ZONE_DAYS);
    setPeriodState(prev => ({ ...prev, startDate, endDate }));

    debouncedRetrieveRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versionState.selectedVersion]);

  useEffect(() => {
    if (versionState.versionCd) return;

    debouncedRetrieveRef.current.cancel();

    const api = gridRef.current?.api;
    if (api) {
      GridUtils.setColumnDefs({ api, columnDefs: fixedColumnDefs, viewName, initState: false });
      applyColumnOrder(api, fixedColumnDefs);
    }
    setRowData([]);
    clearChart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versionState.versionCd]);

  // 화면 전체 조회 버튼을 없앤 대신, 선택형 조건(버전/plant/기간/버킷/품목구분)이 바뀌면 값이
  // 준비되는 즉시 자동 조회한다(§A-9b). 자유 텍스트(itemCdNm)는 SearchOptsGroupRetrieve 내부의
  // 수동 커밋(Enter/검색아이콘)으로만 조회된다.
  useEffect(() => {
    const version = searchStateRef.current.versionState;
    const period = searchStateRef.current.periodState;
    if (!version.planScope || !version.versionCd) return;
    if (!period.isEntirePlanPeriod && (!period.startDate || !period.endDate)) return;

    debouncedRetrieveRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    periodState.startDate, periodState.endDate, periodState.bucketType, periodState.isEntirePlanPeriod,
    versionState.plantCd, itemClassCd
  ]);

  const pivotConfig = useMemo(() => ({
    groupKey: "analysisIndicator",
    chartSeries: INDICATOR_ORDER.map(indicator => {
      const conf = INDICATOR_CONFIG[indicator];
      return conf.type === "line"
        ? {
          field: indicator, text: indicator, type: "line", groupName: t("QTY"),
          typeOptions: { stroke: conf.color, ...(conf.dashed ? { strokeDasharray: "5 3" } : {}), name: t(indicator) }
        }
        : {
          field: indicator, text: indicator, type: "bar", groupName: t("QTY"),
          typeOptions: { stackId: conf.stackId, fill: conf.color, name: t(indicator) }
        };
    }),
    include: chartLabels,
    // x축(주+월 등 버킷이 섞이면) 기본 사전순 정렬을 chartLabels 원래 순서로 덮어쓴다
    // (common/chart.js, fp-report-screen-guide.md §A-7 — 크로스탭 차트 화면 공통).
    xAxisSortMap: buildXAxisSortMap(chartLabels)
  }), [chartLabels, t]);

  return (
    <>
      <FilterContainer>
        <VersionSearchGroup
          value={versionState}
          onChange={(partial) => setVersionState(prev => ({ ...prev, ...partial }))}
        />
        <PeriodSearchGroup
          value={periodState}
          onChange={(partial) => setPeriodState(prev => ({ ...prev, ...partial }))}
          // Report 화면 bucketCase 배정표(fp-report-screen-guide.md §A-3) — ResProdPlan/
          // ResrcUtilization 2개 화면만 WITHOUT_HOUR, 이 화면은 그 예외에 없으므로 WITH_HOUR.
          bucketCase={BUCKET_CASE.WITH_HOUR}
        />
        {/* 조회 버튼 없음(§A-9b, Report 화면 공통 정책) — 자유 텍스트는 필드 안의 Enter/검색아이콘으로
            수동 커밋. */}
        <SearchOptsGroupRetrieve
          fieldRefs={{ itemCdNm: itemCdNmRef }}
          fieldKeys={SEARCH_FIELD_KEYS}
          labels={SEARCH_FIELD_LABELS}
          retrieve={retrieveProdSalesStockStatus}
        />
        {/* old InputField(type="select", name="itemClass") — SearchOptsGroup과 별개인 독립 콤보.
            Master SearchOptsGroup.js의 select 케이스와 동일한 sx(ml/minWidth)를 재사용한다(§A-3b). */}
        <FormControl size="small" sx={{ ml: 1.5, minWidth: 150 }}>
          <InputLabel id="prod-sales-stock-status-item-class-label">{t("FP_ITEM_CLASS_CD")}</InputLabel>
          <Select
            labelId="prod-sales-stock-status-item-class-label"
            label={t("FP_ITEM_CLASS_CD")}
            value={itemClassCd}
            onChange={(e) => setItemClassCd(e.target.value)}
          >
            {ITEM_CLASS_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{t(o.label)}</MenuItem>)}
          </Select>
        </FormControl>
        <Box sx={{ display: "flex" }}>
          <DownloadButton
            onClick={() => gridRef.current?.api?.exportDataAsExcel({ fileName: `${t(title)}.xlsx`, sheetName: t(title) })}
          />
        </Box>
      </FilterContainer>
      {/* old VerticalGridChartBox의 sizes={[50, 50]}(direction="vertical") 대응 — SplitterContainer의
          기본 initialSizes([3, 2], 60/40)를 그대로 두면 그리드:차트가 1:1이 아니게 된다(§A-1c). */}
      <SplitterContainer initialSizes={[1, 1]}>
        <Box id="fp-prod-sales-stock-status-grid" className="ag-theme-balham grid-area" sx={{ height: "calc(100% - 8px)", pb: 1 }}>
          <AgGridReact
            ref={gridRef}
            rowData={rowData}
            {...defaultGridMemo}
            statusBar={statusBar}
            onGridReady={onGridReady}
            onRowDataUpdated={onRowDataUpdated}
            onCellClicked={onCellClicked}
          />
        </Box>
        <Box className="chart-area" sx={{ height: "calc(100% - 8px)", mt: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          {/* old ProdSalesStockStatusChart.jsx는 ChartLegendPlugin을 plugins 배열에 실제로 등록해
              범례를 보여줬다(`plugins={[ChartLegendPlugin, ChartAxisTitlePlugin]}`) — PlanResult와
              달리 "옵션은 있지만 plugins에 없어 실제로는 없었던" 경우가 아니므로(§A-7 "ChartLegendPlugin
              판단 시 주의" 참고), recharts 기본 <Legend/>를 CSS로 숨기지 않고 그대로 노출한다. 클릭 시
              series 숨김+취소선(old ChartLegendPlugin의 인터랙션)은 아래 TODO로 Tier2 유보한다. */}
          <Box sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
            {/* TODO(MIG-T2): [prodsalesstockstatus/ProdSalesStockStatus] 차트 장식성 인터랙션 3종 미이관.
                  old: ChartHatchPatternPlugin(빗금), highlightChartByLabel/resetChartHighlighting(범례
                       hover 시 나머지 dim), 범례 클릭 시 series 숨김+취소선(ChartLegendPlugin)
                  사유: recharts 기본 Legend/PivotChartComposed에 대응 훅이 없음(가이드 §A-7 Tier2 확정 항목) */}
            {/* TODO(MIG-T2): [prodsalesstockstatus/ProdSalesStockStatus] 인벤토리(Route) 그룹합계
                  ("Route Total") 행 미이관.
                  old: itemGroup.childs 중 inventoryCd 컬럼에 groupFooter:{text:"Route Total"} —
                       인벤토리 그룹마다 합계 행을 그린다
                  사유: 이 화면(비상세, isDetail=false)의 백엔드 응답(ProdSalesStockStatusService#getPsiData)은
                       그룹당 4개 analysisIndicator 행만 반환하고 합계용 synthetic 행을 만들지 않는다.
                       AG Grid groupIncludeFooter는 Report 카테고리 어느 화면에도 아직 쓰인 적이 없어
                       프론트 단독으로 새 계산 로직(피벗 컬럼별 합산)을 얹는 것은 백엔드와 합의 없이
                       하는 임의 구현이 된다 — 백엔드가 합계 행을 내려줄지, 프론트 groupIncludeFooter로
                       클라 합산할지 2차에 함께 결정한다. */}
            <PivotChartComposed
              pivotConfig={pivotConfig}
              data={chartData}
              options={{ tooltipContentStyle: { fontSize: "12px" }, minHeight: "0px", decimalPoint: true }}
              showConfigurationPopup={false}
              nullConvert
              customTooltip={AbsValueTooltip}
            />
          </Box>
        </Box>
      </SplitterContainer>
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  );
};

export default withTranslation()(ProdSalesStockStatus);
